import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { reviewCycleKeys, reviewerRequestKeys, reviewTaskKeys } from "@/lib/query";

/**
 * Self-service reviewer requests — EPIC-058. An employee asks off a review
 * they're assigned to, or asks onto one for someone they worked with; HR
 * decides from one queue.
 */

async function failure(res: Response, fallback: string): Promise<Error> {
  const json = (await res.json().catch(() => null)) as { error?: unknown } | null;
  return new Error(typeof json?.error === "string" && json.error ? json.error : fallback);
}

export interface ReviewerRequestSummary {
  id: string;
  reviewId: string;
  requestType: "add" | "remove";
  subjectName: string;
  requesterName: string;
  relationshipName: string | null;
  reason: string;
  statusKey: "pending" | "approved" | "declined";
  decisionNote: string | null;
  decidedAt: string | null;
  createdAt: string;
}

/** The caller's own requests, whatever their status — WI-206 AC-583. */
export function useMyReviewerRequests() {
  return useQuery({
    queryKey: reviewerRequestKeys.mine,
    queryFn: async (): Promise<ReviewerRequestSummary[]> => {
      const res = await fetch("/api/reviewer-requests/mine");
      if (!res.ok) throw await failure(res, "Failed to load your requests");
      return (await res.json()).data;
    },
  });
}

/** Ask off a review — WI-206. */
export function useRequestReviewerRemoval() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ reviewId, reason }: { reviewId: string; reason: string }) => {
      const res = await fetch(`/api/reviews/${reviewId}/reviewer-requests/remove`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason }),
      });
      if (!res.ok) throw await failure(res, "Couldn't send that request");
      return (await res.json()).data as { requestId: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewerRequestKeys.mine });
      toast.success("Request sent — HR will decide, and the review stays yours until then");
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

export interface OpenReviewSubject {
  reviewId: string;
  subjectName: string;
  roleName: string;
}

/** Who the caller could ask to review — WI-207 AC-584. */
export function useOpenReviewSubjects(search: string) {
  return useQuery({
    queryKey: reviewerRequestKeys.openSubjects(search),
    queryFn: async (): Promise<OpenReviewSubject[]> => {
      const res = await fetch(
        `/api/reviewer-requests/open-subjects?search=${encodeURIComponent(search)}`,
      );
      if (!res.ok) throw await failure(res, "Failed to search");
      return (await res.json()).data;
    },
  });
}

/** Ask onto a review for someone you worked with — WI-207. */
export function useRequestReviewerAddition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ reviewId, reason }: { reviewId: string; reason: string }) => {
      const res = await fetch("/api/reviewer-requests/add", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reviewId, reason }),
      });
      if (!res.ok) throw await failure(res, "Couldn't send that request");
      return (await res.json()).data as { requestId: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewerRequestKeys.mine });
      toast.success(
        "Request sent — HR will decide, and it'll show up on your task list if approved",
      );
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

/** HR's approval queue for a round — WI-208. */
export function useReviewerRequestQueue(cycleId: string | null) {
  return useQuery({
    queryKey: reviewerRequestKeys.queue(cycleId ?? ""),
    queryFn: async (): Promise<ReviewerRequestSummary[]> => {
      const res = await fetch(`/api/review-cycles/${cycleId}/reviewer-requests`);
      if (!res.ok) throw await failure(res, "Failed to load reviewer requests");
      return (await res.json()).data;
    },
    enabled: Boolean(cycleId),
  });
}

/**
 * Approve or decline a request — WI-208. Invalidates broadly: an approval
 * changes the roster (and so the review report and the task list), not just
 * the queue it was decided from.
 */
export function useDecideReviewerRequest(cycleId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      requestId,
      decision,
      decisionNote,
    }: {
      requestId: string;
      decision: "approved" | "declined";
      decisionNote: string | null;
    }) => {
      const res = await fetch(`/api/reviewer-requests/${requestId}/decide`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ decision, decisionNote }),
      });
      if (!res.ok) throw await failure(res, "Couldn't record that decision");
      return res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: reviewerRequestKeys.queue(cycleId ?? "") });
      queryClient.invalidateQueries({ queryKey: reviewCycleKeys.excludedReviewers(cycleId ?? "") });
      queryClient.invalidateQueries({ queryKey: reviewCycleKeys.reviewerWorkload(cycleId ?? "") });
      queryClient.invalidateQueries({ queryKey: reviewTaskKeys.all });
      toast.success(variables.decision === "approved" ? "Request approved" : "Request declined");
    },
    onError: (error: Error) => toast.error(error.message),
  });
}
